import { MStockGlobalErrorUI } from '@/components/organism/mStock/MStockGlobalErrorUI'
import { ErrorUI } from '@components/organism/common/ErrorUI.jsx'
import ChunkErrorBoundary from '@pages/ChunkErrorPage.jsx'
import NetworkErrorFallback from '@pages/NetworkErrorFallback.jsx'
import { queryClient } from '@queries/client.js'
import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router-dom'
import { isAuthExpiredError } from '@utils/isAuthExpiredError.js'
import { useLayoutEffect } from 'react'

export const ErrorPage = ({ error: boundaryError, resetErrorBoundary }) => {
  const { t } = useTranslation()
  const routeError = useRouteError()
  const error = routeError || boundaryError
  const isMStock = ['MSTOCK', 'JB', 'HANA'].includes(__APP_TARGET__)

  const isMStockAuthExpired = isMStock && isAuthExpiredError(error)

  const errorTitle = error?.message || t('error.defaultTitle')
  const errorMessage = error?.description

  const isChunkError =
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('error loading dynamically imported module')

  const handleBoundaryRetry = () => {
    queryClient.clear()

    if (resetErrorBoundary) {
      resetErrorBoundary()
      return
    }

    window.location.reload()
  }

  const handleChunkReload = () => {
    const url = new URL(window.location.href)

    url.searchParams.set('__chunk_reload', Date.now().toString())
    window.location.replace(url.toString())
  }

  const handleAction = (actionType) => {
    switch (error?.action ?? actionType) {
      case 'retry':
        handleBoundaryRetry()
        break

      case 'home':
        window.location.replace('/main')
        break

      default:
        break
    }
  }

  const mStockActionMap = {
    retry: {
      buttonLabel: '다시 조회하기',
      handleClick: handleBoundaryRetry
    },

    home: {
      buttonLabel: '메인으로',
      handleClick: () => window.location.replace('/main')
    }
  }

  const buttonLabelMap = {
    retry: t('common.retry'),
    home: t('common.home')
  }

  const mStockAction = mStockActionMap[error?.action]
  const buttonLabel = buttonLabelMap[error?.action] ?? ''

  useLayoutEffect(() => {
    if (isMStockAuthExpired) {
      window.location.replace('/?bridge=N&companyCode=00000011')
    }
  }, [isMStockAuthExpired])

  // MStock chunk
  if (isMStock && isChunkError) {
    return <MStockGlobalErrorUI buttonLabel="다시 조회하기" onRetry={handleChunkReload} />
  }

  if (isMStockAuthExpired) {
    return null
  }

  if (isMStock && error?.status === 404) {
    return (
      <MStockGlobalErrorUI
        title="선택하신 정보를 찾을 수 없습니다."
        buttonLabel="메인으로"
        onRetry={mStockActionMap.home.handleClick}
      />
    )
  }

  // MStock 기타 에러
  if (isMStock) {
    return (
      <MStockGlobalErrorUI
        buttonLabel={mStockAction?.buttonLabel}
        onRetry={mStockAction?.handleClick}
      />
    )
  }

  // chunk 에러
  if (isChunkError) {
    return <ChunkErrorBoundary resetErrorBoundary={resetErrorBoundary} />
  }

  // 네트워크 에러
  if (error?.code === 'NETWORK') {
    return <NetworkErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
  }

  // 페이지 없음
  if (error?.status === 404) {
    return (
      <ErrorUI
        title={t('error.notFound.title')}
        message={t('error.notFound.message')}
        buttonLabel={buttonLabelMap.home}
        handleClick={() => handleAction('home')}
      />
    )
  }

  return (
    <ErrorUI
      title={errorTitle}
      message={errorMessage}
      buttonLabel={buttonLabel}
      handleClick={handleAction}
    />
  )
}
